import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ReportService } from '../../../core/services/reportService/report.service';
import { constantTxt } from '../../../core/constants/constants';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-report-item-detail',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NzInputModule,
    NzSelectModule,
    NzButtonModule,
    NzTableModule,
    NzCheckboxModule,
    NzPaginationModule,
  ],
  templateUrl: './report-item-detail.component.html',
  styleUrl: './report-item-detail.component.less',
})
export class ReportItemDetailComponent implements OnInit, OnDestroy {
  locations: any[] = [];
  purchasingOffices: any[] = [];
  buyers: any[] = [];
  owners: any[] = [];
  ccns: any[] = [];
  reports: any[] = [];
  showGrid = false;

  // Buyer search functionality
  filteredBuyers: any[] = [];
  buyerSearchText: string = '';
  isDropdownSelecting: boolean = false;

  // Request By (Owner) search functionality
  filteredOwners: any[] = [];
  ownerSearchText: string = '';
  isOwnerDropdownSelecting: boolean = false;

  filters = {
    location: 'ALL',
    status: '',
    purchasingOffice: 'ALL',
    buyer: 'ALL',
    owner: 'ALL',
    ccn: 'ALL',
    itemDescription: '',
  };

  statusOptions = [
    { key: 'ALL', label: 'ALL' },
    { key: 'DRAFT', label: 'DRAFT' },
    { key: 'OPEN', label: 'OPEN' },
    { key: 'IN PROGRESS', label: 'IN PROGRESS' },
    { key: 'PARTIAL QUOTED', label: 'PARTIAL QUOTED' },
    { key: 'CLOSED', label: 'CLOSED' },
    { key: 'CANCELED', label: 'CANCELED' },
  ];
  selectedStatuses: string[] = [];
  constants = constantTxt;
  pageIndex = 1;
  pageSize = 20;
  pagedReports: any[] = [];
  customNoDataMessage = constantTxt.noDataFound;

  getLocationsSubscription!: Subscription;
  getPurchasingListSubscription!: Subscription;
  getBuyersSubscription!: Subscription;
  getOwnersSubscription!: Subscription;
  getCCNListSubscription!: Subscription;
  exportItemDetailsExcelReportSubscription!: Subscription;
  getItemDetailsReportSubscription!: Subscription;

  constructor(private readonly reportService: ReportService) {}

  ngOnInit() {
    this.loadLocations();
    this.loadPurchasingOffices();
    this.loadBuyers();
    this.loadOwners();
    this.loadCCNs();
  }

  loadLocations() {
    this.getLocationsSubscription = this.reportService
      .getLocations()
      .subscribe({
        next: (res) => {
          let data = res.body;
          this.locations = data || [];
        },
      });
  }

  loadPurchasingOffices() {
    this.getPurchasingListSubscription = this.reportService
      .getPurchasingList()
      .subscribe({
        next: (res) => {
          let data = res.body;
          this.purchasingOffices = data || [];
        },
      });
  }

  loadBuyers() {
    this.getBuyersSubscription = this.reportService
      .getBuyers(this.filters.purchasingOffice)
      .subscribe({
        next: (res) => {
          let data = res.body;
          this.buyers = data || [];
          this.filteredBuyers = [...this.buyers];
          this.updateBuyerSearchTextFromDropdown();
        },
      });
  }

  loadOwners() {
    this.getOwnersSubscription = this.reportService
      .getOwners(this.filters.purchasingOffice)
      .subscribe({
        next: (res) => {
          let data = res.body;
          const rawOwners = data || [];

          // Remove duplicates based on pnaCreateBy (email) and prefer uppercase names
          const ownerMap = new Map<string, any>();

          rawOwners.forEach((owner) => {
            const existingOwner = ownerMap.get(owner.pnaCreateBy);

            if (!existingOwner) {
              // First occurrence, add it
              ownerMap.set(owner.pnaCreateBy, owner);
            } else {
              // Duplicate found, keep the one with uppercase name (more uppercase characters)
              const currentUpperCount = (owner.pnaRequestor || '').replace(
                /[^A-Z]/g,
                ''
              ).length;
              const existingUpperCount = (
                existingOwner.pnaRequestor || ''
              ).replace(/[^A-Z]/g, '').length;

              if (currentUpperCount > existingUpperCount) {
                ownerMap.set(owner.pnaCreateBy, owner);
              }
            }
          });

          this.owners = Array.from(ownerMap.values());
          this.filteredOwners = this.owners.slice();
          this.updateOwnerSearchTextFromDropdown();
        },
      });
  }

  loadCCNs() {
    this.getCCNListSubscription = this.reportService.getCCNList().subscribe({
      next: (res) => {
        let data = res.body;
        this.ccns = data || [];
      },
    });
  }

  onPurchasingOfficeChange() {
    this.filters.buyer = 'ALL';
    this.filters.owner = 'ALL';
    this.loadBuyers();
    this.loadOwners();
  }

  onStatusChange(status: string, checked: boolean) {
    if (checked) {
      if (!this.selectedStatuses.includes(status)) {
        this.selectedStatuses.push(status);
      }
    } else {
      this.selectedStatuses = this.selectedStatuses.filter((s) => s !== status);
    }
  }

  onPageIndexChange(newIndex: number) {
    this.pageIndex = newIndex;
  }

  onPageSizeChange(newSize: number) {
    this.pageSize = newSize;
    this.pageIndex = 1;
  }

  generateHtmlReport() {
    this.loadReports();
  }

  generateExcelReport() {
    if (this.selectedStatuses.length === 0) {
      alert("You didn't choose Status");
      return;
    }
    this.filters.status = this.selectedStatuses.join(',');
    const params = {
      Location: this.filters.location,
      Status: this.processStatus(this.filters.status),
      PurchasingOffice: this.filters.purchasingOffice,
      Buyer: this.filters.buyer,
      Owner: this.filters.owner,
      CCN: this.filters.ccn,
      ItemDescription: this.filters.itemDescription,
    };

    this.exportItemDetailsExcelReportSubscription = this.reportService
      .exportItemDetailsExcelReport(params)
      .subscribe({
        next: (res) => {
          let blob = res as unknown as Blob;
          if (blob) {
            const a = document.createElement('a');
            const objectUrl = URL.createObjectURL(blob);
            a.href = objectUrl;
            a.download = 'PNA_Report.xlsx';
            a.click();
            URL.revokeObjectURL(objectUrl);
          }
        },
        error: () => {
          alert('Failed to export Excel report.');
        },
      });
  }

  loadReports() {
    if (this.selectedStatuses.length === 0) {
      alert("You didn't choose Status");
      return;
    }
    this.filters.status = this.selectedStatuses.join(',');
    const params = {
      location: this.filters.location,
      status: this.processStatus(this.filters.status),
      purchasingOffice: this.filters.purchasingOffice,
      buyer: this.filters.buyer,
      owner: this.filters.owner,
      ccn: this.filters.ccn,
      itemDescription: this.filters.itemDescription,
    };

    this.getItemDetailsReportSubscription = this.reportService
      .getItemDetailsReport(params)
      .subscribe({
        next: (res) => {
          let data = res.body;
          if (data) {
            this.reports = data;
            this.showGrid = true;
          } else {
            this.reports = [];
          }
        },
        error: () => {
          this.reports = [];
        },
      });
  }

  processStatus(statusString: string): string {
    const statuses = statusString.split(',').map((s) => s.trim());
    if (statuses.includes('ALL')) {
      return 'ALL';
    }
    return statusString;
  }

  getPriorityColor(priority: string): string {
    switch (priority) {
      case 'Fast possible sourcing':
        return 'lightcoral';
      case "Don't delay":
        return 'gold';
      default:
        return 'lightgreen';
    }
  }

  // Buyer Search and Dropdown Synchronization Methods
  updateBuyerSearchTextFromDropdown() {
    if (this.filters.buyer === 'ALL') {
      this.buyerSearchText = 'ALL';
    } else {
      let selectedBuyer = this.buyers.find(
        (buyer) => buyer.userId === this.filters.buyer
      );
      if (!selectedBuyer) {
        selectedBuyer = this.buyers.find(
          (buyer) => buyer.id === this.filters.buyer
        );
      }
      if (!selectedBuyer) {
        selectedBuyer = this.buyers.find(
          (buyer) => buyer.buyerId === this.filters.buyer
        );
      }
      if (!selectedBuyer) {
        selectedBuyer = this.buyers.find(
          (buyer) => buyer.userId === parseInt(this.filters.buyer)
        );
      }

      if (selectedBuyer) {
        const fullName =
          selectedBuyer.fullName ||
          selectedBuyer.name ||
          selectedBuyer.buyerName ||
          selectedBuyer.displayName;
        this.buyerSearchText = fullName;
      }
    }
  }

  onBuyerSearchChange() {
    if (this.isDropdownSelecting) {
      return;
    }

    if (
      !this.buyerSearchText ||
      this.buyerSearchText.trim() === '' ||
      this.buyerSearchText.toUpperCase() === 'ALL'
    ) {
      this.filteredBuyers = [...this.buyers];
      this.filters.buyer = 'ALL';
    } else {
      this.filteredBuyers = this.buyers.filter(
        (buyer) =>
          buyer.fullName &&
          buyer.fullName
            .toLowerCase()
            .includes(this.buyerSearchText.toLowerCase())
      );

      if (this.filteredBuyers.length === 1) {
        this.filters.buyer = this.filteredBuyers[0].userId;
      } else if (this.filteredBuyers.length === 0) {
        this.filters.buyer = 'ALL';
      }
    }
  }

  onBuyerSearchBlur() {
    if (this.isDropdownSelecting) {
      return;
    }

    if (!this.buyerSearchText || this.buyerSearchText.trim() === '') {
      this.buyerSearchText = 'ALL';
      this.filters.buyer = 'ALL';
      this.filteredBuyers = [...this.buyers];
      return;
    }

    if (this.filteredBuyers.length === 1) {
      const selectedBuyer = this.filteredBuyers[0];
      const fullName =
        selectedBuyer.fullName ||
        selectedBuyer.name ||
        selectedBuyer.buyerName ||
        selectedBuyer.displayName;
      this.buyerSearchText = fullName;
      this.filters.buyer = selectedBuyer.userId;
      this.filteredBuyers = [...this.buyers];
    } else if (this.filteredBuyers.length === 0) {
      this.buyerSearchText = 'ALL';
      this.filters.buyer = 'ALL';
      this.filteredBuyers = [...this.buyers];
    } else if (this.filteredBuyers.length > 1) {
      if (this.filters.buyer !== 'ALL') {
        const currentSelection = this.buyers.find(
          (buyer) => buyer.userId === this.filters.buyer
        );
        if (currentSelection) {
          const fullName =
            currentSelection.fullName ||
            currentSelection.name ||
            currentSelection.buyerName ||
            currentSelection.displayName;
          this.buyerSearchText = fullName;
        }
      } else {
        this.buyerSearchText = 'ALL';
      }
      this.filteredBuyers = [...this.buyers];
    }
  }

  onBuyerDropdownChange() {
    this.isDropdownSelecting = true;
    this.updateBuyerSearchTextFromDropdown();
    this.filteredBuyers = [...this.buyers];
    setTimeout(() => {
      this.isDropdownSelecting = false;
    }, 100);
  }

  // Request By (Owner) Search and Dropdown Synchronization Methods
  updateOwnerSearchTextFromDropdown() {
    if (this.filters.owner === 'ALL') {
      this.ownerSearchText = 'ALL';
    } else {
      // Since pnaCreateBy is a string, no need to parse to integer
      let selectedOwner = this.owners.find(
        (owner) => owner.pnaCreateBy === this.filters.owner
      );

      if (selectedOwner) {
        const requestorName =
          selectedOwner.pnaRequestor ||
          selectedOwner.name ||
          selectedOwner.ownerName ||
          selectedOwner.displayName;
        this.ownerSearchText = requestorName;
      }
    }
  }

  onOwnerSearchChange() {
    if (this.isOwnerDropdownSelecting) {
      return;
    }

    if (
      !this.ownerSearchText ||
      this.ownerSearchText.trim() === '' ||
      this.ownerSearchText.toUpperCase() === 'ALL'
    ) {
      this.filteredOwners = this.owners.slice();
      this.filters.owner = 'ALL';
    } else {
      const filtered = this.owners.filter(
        (owner) =>
          owner.pnaRequestor &&
          owner.pnaRequestor
            .toLowerCase()
            .includes(this.ownerSearchText.toLowerCase())
      );

      // Force new array reference to trigger change detection
      this.filteredOwners = [...filtered];

      if (this.filteredOwners.length === 1) {
        this.filters.owner = this.filteredOwners[0].pnaCreateBy;
      } else if (this.filteredOwners.length === 0) {
        this.filters.owner = 'ALL';
      }
    }
  }

  onOwnerSearchBlur() {
    if (this.isOwnerDropdownSelecting) {
      return;
    }

    if (!this.ownerSearchText || this.ownerSearchText.trim() === '') {
      this.ownerSearchText = 'ALL';
      this.filters.owner = 'ALL';
      this.filteredOwners = this.owners.slice();
      return;
    }

    if (this.filteredOwners.length === 1) {
      const selectedOwner = this.filteredOwners[0];
      const requestorName =
        selectedOwner.pnaRequestor ||
        selectedOwner.name ||
        selectedOwner.ownerName ||
        selectedOwner.displayName;
      this.ownerSearchText = requestorName;
      this.filters.owner = selectedOwner.pnaCreateBy;
      this.filteredOwners = this.owners.slice();
    } else if (this.filteredOwners.length === 0) {
      this.ownerSearchText = 'ALL';
      this.filters.owner = 'ALL';
      this.filteredOwners = this.owners.slice();
    } else if (this.filteredOwners.length > 1) {
      if (this.filters.owner !== 'ALL') {
        const currentSelection = this.owners.find(
          (owner) => owner.pnaCreateBy === this.filters.owner
        );
        if (currentSelection) {
          const requestorName =
            currentSelection.pnaRequestor ||
            currentSelection.name ||
            currentSelection.ownerName ||
            currentSelection.displayName;
          this.ownerSearchText = requestorName;
        }
      } else {
        this.ownerSearchText = 'ALL';
      }
      this.filteredOwners = this.owners.slice();
    }
  }

  onOwnerDropdownChange() {
    this.isOwnerDropdownSelecting = true;
    this.updateOwnerSearchTextFromDropdown();
    this.filteredOwners = this.owners.slice();
    setTimeout(() => {
      this.isOwnerDropdownSelecting = false;
    }, 100);
  }

  trackByOwner(_index: number, owner: any): any {
    return owner.pnaCreateBy;
  }

  ngOnDestroy(): void {
    this.getLocationsSubscription?.unsubscribe();
    this.getPurchasingListSubscription?.unsubscribe();
    this.getBuyersSubscription?.unsubscribe();
    this.getOwnersSubscription?.unsubscribe();
    this.getCCNListSubscription?.unsubscribe();
    this.exportItemDetailsExcelReportSubscription?.unsubscribe();
    this.getItemDetailsReportSubscription?.unsubscribe();
  }
}
